// Workspace.attribution.ts — signup-attribution capture for the register funnel.
//
// The leadsales.io collector (mu-plugin v1.2) decorates every register link
// with the visitor's attribution: last-touch utm_*, click IDs, referrer,
// landing/CTA pages, a rolling page journey, first-touch ls_ft_* params and
// the HubSpot visitor token (ls_hutk). This module reads those URL params ONCE
// into a single atomic blob that survives the funnel (localStorage — in-SPA
// navigations drop query params and the Google sign-in redirect leaves the
// page entirely), and is sent in the workspace-creation payload (write-once,
// registration-scoped; the backend enforces attribution-once).
//
// Contract: keys/caps mirror bff-web WorkspaceAttribution and
// workspace-service CreateWorkspaceRequest — see leadsales-io-site/README.md
// (workspace root) for the param dictionary. Values are opaque validated
// strings; never cast them to enums (truthy-invalid-enum learning). NULL /
// absent means "unattributed" — never fabricate defaults here.

import { hostnameMatchesDomain } from 'src/functions/url.functions';
import { aiAssistantFromHost } from './Workspace.aiAssistant';

export const ATTRIBUTION_STORAGE_KEY = 'workspaceAttribution';

// Mirrors the collector's first-touch window: an older stash is stale and
// must be treated as absent rather than attached to a signup.
const ATTRIBUTION_TTL_DAYS = 90;

// Mirror backend caps (bff-web `max=200`, workspace-service `max:200`) so a
// clipped value here can never turn into a 422 at a later layer.
const MAX_VALUE_LENGTH = 200;
const MAX_PAGES = 5;

export type tWorkspaceAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
  msclkid?: string;
  ref?: string;
  land?: string;
  page?: string;
  pages?: string[];
  ft_source?: string;
  ft_medium?: string;
  ft_campaign?: string;
  ft_ref?: string;
  ft_land?: string;
  ft_date?: string;
  hutk?: string;
  captured_at: string;
};

type tScalarAttributionKey = Exclude<
  keyof tWorkspaceAttribution,
  'pages' | 'captured_at'
>;

// URL param → blob key. Anything not named here is ignored by design.
const SCALAR_PARAM_TO_KEY: Record<string, tScalarAttributionKey> = {
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_content: 'utm_content',
  utm_term: 'utm_term',
  fbclid: 'fbclid',
  gclid: 'gclid',
  ttclid: 'ttclid',
  msclkid: 'msclkid',
  ls_ref: 'ref',
  ls_land: 'land',
  ls_page: 'page',
  ls_ft_source: 'ft_source',
  ls_ft_medium: 'ft_medium',
  ls_ft_campaign: 'ft_campaign',
  ls_ft_ref: 'ft_ref',
  ls_ft_land: 'ft_land',
  ls_ft_date: 'ft_date',
  ls_hutk: 'hutk',
};

// GRO-913: params that mark a real campaign touch. Meta ads deep-link straight
// to /auth/sign-up, so these arrive WITHOUT any collector decoration — the
// visit never passed through leadsales.io and therefore has no `ls_*` params.
const DIRECT_AD_TRIGGER_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
];

// GRO-913: fields only the site collector can observe — the HubSpot visitor
// token and the first-touch set it tracks across the 90-day window. A direct-ad
// landing cannot reconstruct either, so they are carried forward from an
// existing stash rather than destroyed when the ad becomes the new last touch.
const SITE_ONLY_KEYS: tScalarAttributionKey[] = [
  'hutk',
  'ft_source',
  'ft_medium',
  'ft_campaign',
  'ft_ref',
  'ft_land',
  'ft_date',
];

// Our own hosts are not a referrer: an in-SPA navigation or a bounce back from
// a Leadsales property must never be recorded as the traffic source.
const INTERNAL_REFERRER_HOSTS = ['leadsales.io', 'leadsales.services'];

// Precomputed once — `captureDirectAdAttribution` runs on every URL change and
// the collector-only params are exactly the `ls_`-prefixed ones.
const ALL_PARAM_ENTRIES = Object.entries(SCALAR_PARAM_TO_KEY);
const DIRECT_AD_PARAM_ENTRIES = ALL_PARAM_ENTRIES.filter(
  ([param]) => !param.startsWith('ls_'),
);

function clip(value: string): string {
  return value.trim().slice(0, MAX_VALUE_LENGTH);
}

function readScalarParams(
  searchParams: URLSearchParams,
  entries: [string, tScalarAttributionKey][],
): Partial<tWorkspaceAttribution> {
  const values: Partial<tWorkspaceAttribution> = {};

  entries.forEach(([param, key]) => {
    const raw = searchParams.get(param);
    if (!raw) return;

    const value = clip(raw);
    if (value) {
      values[key] = value;
    }
  });

  return values;
}

function externalReferrerHost(referrer?: string): string | undefined {
  if (!referrer) {
    return undefined;
  }

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname;
  } catch {
    return undefined;
  }

  if (!hostname || hostnameMatchesDomain(hostname, INTERNAL_REFERRER_HOSTS)) {
    return undefined;
  }

  return hostname;
}

// Paths only: query strings can embed emails (search pages) and the backend
// strips them anyway — dropping them here keeps the payload lean and clean.
function stripQueryAndFragment(path: string): string {
  return path.split('?')[0].split('#')[0];
}

/**
 * Builds the attribution blob from the current URL's search params.
 *
 * Sentinel-gated: the collector ALWAYS sets `ls_page` when decorating a link,
 * so its absence means this visit did not arrive through a decorated register
 * link — return undefined and never overwrite an existing stash with a
 * partial/manual param set (chimera-touch guard).
 *
 * Undecorated ad traffic that lands straight on the CRM is handled separately
 * by `captureDirectAdAttribution` (GRO-913), which demands a genuine campaign
 * param rather than relaxing this sentinel — the chimera guard still stands.
 */
export function captureAttribution(
  searchParams: URLSearchParams,
): tWorkspaceAttribution | undefined {
  if (!searchParams.get('ls_page')) {
    return undefined;
  }

  const blob: tWorkspaceAttribution = {
    ...readScalarParams(searchParams, ALL_PARAM_ENTRIES),
    captured_at: new Date().toISOString(),
  };

  const rawPages = searchParams.get('ls_pages');
  if (rawPages) {
    const pages = rawPages
      .split('|')
      .slice(0, MAX_PAGES)
      .map((page) => clip(stripQueryAndFragment(page)))
      .filter(Boolean);

    if (pages.length > 0) {
      blob.pages = pages;
    }
  }

  return blob;
}

export type tDirectAdContext = {
  referrer?: string;
  pathname?: string;
};

/**
 * GRO-913 — fallback capture for ad traffic that never touches leadsales.io.
 *
 * Meta ads (`salesSource=Meta_Register`) deep-link straight to the CRM's
 * `/auth/sign-up` carrying real `utm_*` and click IDs but none of the
 * collector's `ls_*` decoration, so `captureAttribution` drops them on the
 * sentinel. This reads that same ad data off the raw params instead.
 *
 * Deliberate omissions — a direct landing observed exactly ONE touch:
 * - `ft_*` is NOT filled from the ad. Asserting a first touch we never saw
 *   would systematically credit paid for users whose real first touch happened
 *   earlier or elsewhere; an empty first touch is honest and recoverable, an
 *   invented one silently biases channel reporting. When a prior stash exists
 *   its real `ft_*` is carried over by `preserveSiteOnlyFields`.
 * - `pages` stays absent — there is no journey to report, and simulating one
 *   would fabricate navigation that never happened.
 *
 * Returns undefined when the collector already owns this visit (`ls_page`
 * present) or when there is neither a genuine campaign param nor an AI
 * assistant referrer (GRO-1267), so an untagged or internal visit can never
 * manufacture an empty attribution row.
 */
export function captureDirectAdAttribution(
  searchParams: URLSearchParams,
  context: tDirectAdContext = {},
): tWorkspaceAttribution | undefined {
  // Collector-decorated visits belong to captureAttribution — never both.
  if (searchParams.get('ls_page')) {
    return undefined;
  }

  const hasCampaignTouch = DIRECT_AD_TRIGGER_PARAMS.some((param) =>
    clip(searchParams.get(param) ?? ''),
  );
  const ref = externalReferrerHost(context.referrer);
  // GRO-1267: an AI assistant that only sends a referrer (Gemini, Claude,
  // Perplexity, Meta AI…) is a real touch too. Any other bare referrer is
  // still not, so an untagged visit never manufactures an attribution row.
  if (!hasCampaignTouch && !aiAssistantFromHost(ref)) {
    return undefined;
  }

  const blob: tWorkspaceAttribution = {
    ...readScalarParams(searchParams, DIRECT_AD_PARAM_ENTRIES),
    captured_at: new Date().toISOString(),
  };

  if (ref) {
    blob.ref = clip(ref);
  }

  // The ad's landing page IS the CTA page for this touch — same value, since
  // the visitor arrived and converted on the one page we ever saw.
  const landing = clip(stripQueryAndFragment(context.pathname ?? ''));
  if (landing) {
    blob.land = landing;
    blob.page = landing;
  }

  return blob;
}

/**
 * Carries the fields only the site collector can observe (`hutk`, `ft_*`) from
 * an existing stash onto a fresh direct-ad touch.
 *
 * Without this, an ad click after a site visit would overwrite the stash and
 * destroy the HubSpot visitor token — the join key for demo↔signup pairing —
 * along with the real first touch. Set-if-empty: the ad's own last-touch
 * fields always win, and nothing already present on the new blob is replaced.
 */
function preserveSiteOnlyFields(
  blob: tWorkspaceAttribution,
  existing: tWorkspaceAttribution | undefined,
): tWorkspaceAttribution {
  if (!existing) {
    return blob;
  }

  const merged: tWorkspaceAttribution = { ...blob };

  SITE_ONLY_KEYS.forEach((key) => {
    const carried = existing[key];
    if (carried && !merged[key]) {
      merged[key] = carried;
    }
  });

  return merged;
}

export type tCapturedAttribution = {
  collector?: tWorkspaceAttribution;
  directAd?: tWorkspaceAttribution;
};

/**
 * GRO-913 — the single precedence rule for what gets written to the stash.
 *
 * Lives here rather than inline in `Workspace.hook` so the rule is unit-tested
 * instead of hiding as branching inside a provider that needs a full mock tree
 * to exercise.
 *
 * 1. A collector-decorated visit always wins — it is strictly richer (journey,
 *    first touch, `hutk`) than anything a bare ad landing can carry.
 * 2. Otherwise a direct-ad touch becomes the new last touch, but inherits the
 *    site-only fields from whatever is already stashed, so the pairing key
 *    survives an ad click that follows a site visit.
 * 3. Neither present → undefined, and the caller leaves the stash untouched.
 *    Absent stays absent; nothing here ever fabricates an empty attribution.
 */
export function resolveAttributionWrite(
  captured: tCapturedAttribution,
  stored?: tWorkspaceAttribution,
): tWorkspaceAttribution | undefined {
  if (captured.collector) {
    return captured.collector;
  }

  if (captured.directAd) {
    return preserveSiteOnlyFields(captured.directAd, stored);
  }

  return undefined;
}

/**
 * A stash older than the collector's 90-day window (or with an unparseable
 * timestamp) must be treated as absent — stale attribution is worse than none.
 */
export function isAttributionFresh(
  blob: tWorkspaceAttribution | undefined,
): blob is tWorkspaceAttribution {
  if (!blob?.captured_at) {
    return false;
  }

  const capturedAt = Date.parse(blob.captured_at);
  if (Number.isNaN(capturedAt)) {
    return false;
  }

  return Date.now() - capturedAt <= ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Raw-localStorage reader for consumers outside the WorkspaceProvider tree
 * (e.g. `useAnalyticsAuth.identifyUser`, which runs before WorkspaceProvider
 * state exists and after the mid-registration `posthog.reset()`). Returns
 * undefined for missing, unparseable, or stale stashes.
 */
export function readStoredAttribution(): tWorkspaceAttribution | undefined {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as tWorkspaceAttribution;
    return isAttributionFresh(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}
